import React, { useState, useEffect } from 'react';
import { 
  Row, Col, Card, Button, Table, Badge, Modal, Form, Alert, Spinner, 
  InputGroup, ProgressBar, Tabs, Tab 
} from 'react-bootstrap';
import { 
  FaUserShield, FaUsers, FaChartLine, FaCog, FaDatabase, FaServer, 
  FaPlus, FaEdit, FaTrash, FaKey, FaSearch, FaDownload, FaShieldAlt,
  FaHeartbeat, FaMemory, FaClock, FaBroom, FaExclamationTriangle,
  FaBuilding, FaCity
} from 'react-icons/fa';
import { useAuth } from '../hooks/useAuth';
import { useApi } from '../context/ApiContext';
import { toast } from 'react-toastify';

const Admin = () => {
  const { user } = useAuth();
  const { apiCall } = useApi();
  
  // States
  const [stats, setStats] = useState(null);
  const [panelUsers, setPanelUsers] = useState([]);
  const [visaOffices, setVisaOffices] = useState([]);
  const [activityLogs, setActivityLogs] = useState([]);
  const [healthStatus, setHealthStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Modal states
  const [showUserModal, setShowUserModal] = useState(false);
  const [showOfficeModal, setShowOfficeModal] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [currentOffice, setCurrentOffice] = useState(null);
  const [modalMode, setModalMode] = useState('create');
  
  // Filter states
  const [userFilters, setUserFilters] = useState({
    role: 'all',
    status: 'all',
    search: ''
  });
  
  // Form states
  const [userFormData, setUserFormData] = useState({
    email: '',
    password: '',
    first_name: '',
    last_name: '',
    phone: '',
    role: 'user',
    office_id: ''
  });
  
  const [officeFormData, setOfficeFormData] = useState({
    name: '',
    city: '',
    address: '',
    phone: '',
    email: '',
    capacity: 50
  });
  
  const [passwordFormData, setPasswordFormData] = useState({
    new_password: ''
  });

  // Role-based access
  const isAdmin = user?.role === 'admin';
  const isVisaOffice = user?.role === 'visa_office';
  const canManageOffices = isAdmin;
  const canCreateUsers = isAdmin || isVisaOffice;

  useEffect(() => {
    loadDashboardData();
    const interval = setInterval(() => {
      loadHealthStatus();
    }, 30000); // Refresh health status every 30 seconds
    
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    loadPanelUsers();
  }, [userFilters]);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      await Promise.all([
        loadStats(),
        loadPanelUsers(),
        loadVisaOffices(),
        loadActivityLogs(),
        loadHealthStatus()
      ]);
      setError(null);
    } catch (err) {
      console.error('Error loading admin data:', err);
      setError(err.message || 'Veri yüklenirken hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    if (!isAdmin) return; // Only admin can see full stats
    const response = await apiCall('GET', '/api/admin/stats');
    setStats(response.data);
  };

  const loadPanelUsers = async () => {
    const response = await apiCall('GET', '/api/admin/panel-users');
    setPanelUsers(response.data || []);
  };

  const loadVisaOffices = async () => {
    const response = await apiCall('GET', '/api/admin/visa-offices');
    setVisaOffices(response.data || []);
  };

  const loadActivityLogs = async () => {
    if (!isAdmin) return; // Only admin can see activity logs
    const response = await apiCall('GET', '/api/admin/activity-logs?limit=20');
    setActivityLogs(response.data || []);
  };

  const loadHealthStatus = async () => {
    const response = await apiCall('GET', '/api/admin/health');
    setHealthStatus(response.data);
  };

  // Office Management
  const handleCreateOffice = () => {
    setCurrentOffice(null);
    setModalMode('create');
    setOfficeFormData({
      name: '',
      city: '',
      address: '',
      phone: '',
      email: '',
      capacity: 50
    });
    setShowOfficeModal(true);
  };

  const handleEditOffice = (office) => {
    setCurrentOffice(office);
    setModalMode('edit');
    setOfficeFormData({
      name: office.name || '',
      city: office.city || '',
      address: office.address || '',
      phone: office.phone || '',
      email: office.email || '',
      capacity: office.capacity || 50
    });
    setShowOfficeModal(true);
  };

  const handleOfficeSubmit = async (e) => {
    e.preventDefault();
    try {
      const endpoint = modalMode === 'create' 
        ? '/api/admin/visa-offices'
        : `/api/admin/visa-offices/${currentOffice.id}`;
      
      const method = modalMode === 'create' ? 'POST' : 'PUT';
      
      await apiCall(method, endpoint, officeFormData);
      
      toast.success(`Danışmanlık firması başarıyla ${modalMode === 'create' ? 'oluşturuldu' : 'güncellendi'}`);
      setShowOfficeModal(false);
      loadVisaOffices();
    } catch (error) {
      toast.error(error.message || 'İşlem başarısız');
    }
  };

  // User Management
  const handleCreateUser = () => {
    setCurrentUser(null);
    setModalMode('create');
    setUserFormData({
      email: '',
      password: '',
      first_name: '',
      last_name: '',
      phone: '',
      role: 'user',
      office_id: isVisaOffice ? user.office_id : ''
    });
    setShowUserModal(true);
  };

  const handleEditUser = (panelUser) => {
    setCurrentUser(panelUser);
    setModalMode('edit');
    setUserFormData({
      email: panelUser.email || '',
      password: '',
      first_name: panelUser.first_name || '',
      last_name: panelUser.last_name || '',
      phone: panelUser.phone || '',
      role: panelUser.role || 'user',
      office_id: panelUser.office_id || ''
    });
    setShowUserModal(true);
  };

  const handleUserSubmit = async (e) => {
    e.preventDefault();
    try {
      const endpoint = modalMode === 'create' 
        ? '/api/admin/panel-users'
        : `/api/admin/panel-users/${currentUser.id}`;
      
      const method = modalMode === 'create' ? 'POST' : 'PUT';
      const data = { ...userFormData };
      
      // Don't send empty password on edit
      if (modalMode === 'edit' && !data.password) {
        delete data.password;
      }
      
      await apiCall(method, endpoint, data);
      
      toast.success(`Kullanıcı başarıyla ${modalMode === 'create' ? 'oluşturuldu' : 'güncellendi'}`);
      setShowUserModal(false);
      loadPanelUsers();
    } catch (error) {
      toast.error(error.message || 'İşlem başarısız');
    }
  };

  const handleDeleteUser = (user) => {
    setCurrentUser(user);
    setShowDeleteModal(true);
  };

  const handleResetPassword = (user) => {
    setCurrentUser(user);
    setPasswordFormData({ new_password: '' });
    setShowPasswordModal(true);
  };

  const handleUserFilterChange = (key, value) => {
    setUserFilters(prev => ({ ...prev, [key]: value }));
  };

  const confirmDeleteUser = async () => {
    try {
      await apiCall('PUT', `/api/admin/panel-users/${currentUser.id}`, { is_active: false });
      toast.success('Kullanıcı deaktive edildi!');
      setShowDeleteModal(false);
      loadPanelUsers();
      loadStats();
    } catch (error) {
      toast.error(`Hata: ${error.message}`);
    }
  };

  const handleCleanup = async (operation) => {
    if (!window.confirm(`${operation} temizlik işlemini gerçekleştirmek istediğinizden emin misiniz?`)) {
      return;
    }
    
    try {
      const response = await apiCall('POST', '/api/admin/cleanup', { operation });
      toast.success(response.data.message);
      loadStats();
    } catch (error) {
      toast.error(`Hata: ${error.message}`);
    }
  };

  const handleInputChange = (e, formType = 'user') => {
    const { name, value } = e.target;
    if (formType === 'user') {
      setUserFormData(prev => ({ ...prev, [name]: value }));
    } else if (formType === 'password') {
      setPasswordFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const getRoleBadge = (role) => {
    const roleMap = {
      'admin': { bg: 'danger', text: 'Yönetici' },
      'visa_office': { bg: 'warning', text: 'Danışmanlık Firması' },
      'user': { bg: 'primary', text: 'Kullanıcı' }
    };
    return roleMap[role] || { bg: 'secondary', text: role };
  };

  const getHealthBadge = (status) => {
    const statusMap = {
      'healthy': { bg: 'success', text: 'Sağlıklı' },
      'warning': { bg: 'warning', text: 'Uyarı' },
      'unhealthy': { bg: 'danger', text: 'Sorunlu' }
    };
    return statusMap[status] || { bg: 'secondary', text: 'Bilinmiyor' };
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
        <Spinner animation="border" variant="primary" />
      </div>
    );
  }

  if (error) {
    return (
      <Alert variant="danger">
        <FaExclamationTriangle className="me-2" />
        {error}
      </Alert>
    );
  }

  return (
    <div className="admin-page">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2>
          <FaUserShield className="me-2" />
          {isAdmin ? 'Sistem Yönetimi' : isVisaOffice ? 'Firma Yönetimi' : 'Kullanıcı Paneli'}
        </h2>
        <div>
          {canCreateUsers && (
            <Button variant="primary" onClick={handleCreateUser} className="me-2">
              <FaPlus className="me-1" />
              Kullanıcı Ekle
            </Button>
          )}
          {canManageOffices && (
            <Button variant="outline-primary" onClick={handleCreateOffice}>
              <FaBuilding className="me-1" />
              Ofis Ekle
            </Button>
          )}
        </div>
      </div>

      <Tabs defaultActiveKey="overview" className="mb-4">
        {/* Overview Tab - Role-based content */}
        <Tab eventKey="overview" title={<><FaChartLine className="me-1" />Genel Bakış</>}>
          <Row className="g-4">
            {/* Stats Cards - Admin only */}
            {isAdmin && stats && (
              <>
                <Col md={3}>
                  <Card className="h-100 border-primary">
                    <Card.Body className="text-center">
                      <FaUsers className="display-4 text-primary mb-2" />
                      <h4>{stats.panel_users?.reduce((acc, curr) => acc + parseInt(curr.count), 0) || 0}</h4>
                      <p className="text-muted mb-0">Panel Kullanıcıları</p>
                    </Card.Body>
                  </Card>
                </Col>
                <Col md={3}>
                  <Card className="h-100 border-success">
                    <Card.Body className="text-center">
                      <FaBuilding className="display-4 text-success mb-2" />
                      <h4>{stats.visa_offices?.total || 0}</h4>
                      <p className="text-muted mb-0">Vize Ofisleri</p>
                    </Card.Body>
                  </Card>
                </Col>
                <Col md={3}>
                  <Card className="h-100 border-warning">
                    <Card.Body className="text-center">
                      <FaUsers className="display-4 text-warning mb-2" />
                      <h4>{stats.visa_users?.total || 0}</h4>
                      <p className="text-muted mb-0">Vize Kullanıcıları</p>
                    </Card.Body>
                  </Card>
                </Col>
                <Col md={3}>
                  <Card className="h-100 border-info">
                    <Card.Body className="text-center">
                      <FaClock className="display-4 text-info mb-2" />
                      <h4>{stats.appointments?.total || 0}</h4>
                      <p className="text-muted mb-0">Randevular</p>
                    </Card.Body>
                  </Card>
                </Col>
              </>
            )}

            {/* Office Info for Visa Office users */}
            {isVisaOffice && (
              <Row className="mb-4">
                <Col>
                  <Card>
                    <Card.Header>
                      <h5>Firma Bilgileri</h5>
                    </Card.Header>
                    <Card.Body>
                      {visaOffices.length > 0 && (
                        <Row>
                          <Col md={6}>
                            <p><strong>Firma Adı:</strong> {visaOffices[0].name}</p>
                            <p><strong>Şehir:</strong> {visaOffices[0].city}</p>
                            <p><strong>Adres:</strong> {visaOffices[0].address}</p>
                          </Col>
                          <Col md={6}>
                            <p><strong>Telefon:</strong> {visaOffices[0].phone}</p>
                            <p><strong>E-posta:</strong> {visaOffices[0].email}</p>
                            <p><strong>Kapasite:</strong> {visaOffices[0].capacity}</p>
                          </Col>
                        </Row>
                      )}
                    </Card.Body>
                  </Card>
                </Col>
              </Row>
            )}

            {/* System Health - Admin only */}
            {isAdmin && healthStatus && (
              <Col md={12}>
                <Card>
                  <Card.Header>
                    <h5 className="mb-0">
                      <FaHeartbeat className="me-2" />
                      Sistem Durumu
                    </h5>
                  </Card.Header>
                  <Card.Body>
                    <Row>
                      <Col md={3}>
                        <div className="text-center">
                          <FaDatabase className="display-6 text-success mb-2" />
                          <h6>Veritabanı</h6>
                          <Badge bg="success">Bağlı</Badge>
                        </div>
                      </Col>
                      <Col md={3}>
                        <div className="text-center">
                          <FaServer className="display-6 text-primary mb-2" />
                          <h6>Sunucu</h6>
                          <Badge bg="primary">Çalışıyor</Badge>
                        </div>
                      </Col>
                      <Col md={3}>
                        <div className="text-center">
                          <FaMemory className="display-6 text-warning mb-2" />
                          <h6>Bellek</h6>
                          <Badge bg="warning">Normal</Badge>
                        </div>
                      </Col>
                      <Col md={3}>
                        <div className="text-center">
                          <FaClock className="display-6 text-info mb-2" />
                          <h6>Çalışma Süresi</h6>
                          <Badge bg="info">{Math.floor(healthStatus.uptime / 3600)}h</Badge>
                        </div>
                      </Col>
                    </Row>
                  </Card.Body>
                </Card>
              </Col>
            )}
          </Row>
        </Tab>

        {/* Users Tab */}
        <Tab eventKey="users" title={<><FaUsers className="me-1" />Kullanıcılar</>}>
          <Card>
            <Card.Header className="d-flex justify-content-between align-items-center">
              <h5 className="mb-0">Panel Kullanıcıları</h5>
              {canCreateUsers && (
                <Button variant="primary" size="sm" onClick={handleCreateUser}>
                  <FaPlus className="me-1" />
                  Yeni Kullanıcı
                </Button>
              )}
            </Card.Header>
            <Card.Body>
              {/* User Filters */}
              <Row className="mb-3">
                <Col md={4}>
                  <InputGroup>
                    <InputGroup.Text><FaSearch /></InputGroup.Text>
                    <Form.Control
                      type="text"
                      placeholder="Kullanıcı ara..."
                      value={userFilters.search}
                      onChange={(e) => setUserFilters(prev => ({ ...prev, search: e.target.value }))}
                    />
                  </InputGroup>
                </Col>
                <Col md={3}>
                  <Form.Select
                    value={userFilters.role}
                    onChange={(e) => setUserFilters(prev => ({ ...prev, role: e.target.value }))}
                  >
                    <option value="all">Tüm Roller</option>
                    <option value="admin">Admin</option>
                    <option value="visa_office">Danışmanlık Firması</option>
                    <option value="user">Kullanıcı</option>
                  </Form.Select>
                </Col>
                <Col md={3}>
                  <Form.Select
                    value={userFilters.status}
                    onChange={(e) => setUserFilters(prev => ({ ...prev, status: e.target.value }))}
                  >
                    <option value="all">Tüm Durumlar</option>
                    <option value="active">Aktif</option>
                    <option value="inactive">Pasif</option>
                  </Form.Select>
                </Col>
              </Row>

              {/* Users Table */}
              <Table responsive striped hover>
                <thead>
                  <tr>
                    <th>Ad Soyad</th>
                    <th>E-posta</th>
                    <th>Rol</th>
                    <th>Ofis</th>
                    <th>Durum</th>
                    <th>Son Giriş</th>
                    <th>İşlemler</th>
                  </tr>
                </thead>
                <tbody>
                  {panelUsers
                    .filter(user => {
                      const matchesSearch = !userFilters.search || 
                        `${user.first_name} ${user.last_name}`.toLowerCase().includes(userFilters.search.toLowerCase()) ||
                        user.email.toLowerCase().includes(userFilters.search.toLowerCase());
                      const matchesRole = userFilters.role === 'all' || user.role === userFilters.role;
                      const matchesStatus = userFilters.status === 'all' || 
                        (userFilters.status === 'active' && user.is_active) ||
                        (userFilters.status === 'inactive' && !user.is_active);
                      return matchesSearch && matchesRole && matchesStatus;
                    })
                    .map(user => (
                      <tr key={user.id}>
                        <td>{user.first_name} {user.last_name}</td>
                        <td>{user.email}</td>
                        <td>
                          <Badge bg={
                            user.role === 'admin' ? 'danger' : 
                            user.role === 'visa_office' ? 'warning' : 'primary'
                          }>
                            {user.role === 'admin' ? 'Admin' : 
                             user.role === 'visa_office' ? 'Danışmanlık Firması' : 'Kullanıcı'}
                          </Badge>
                        </td>
                        <td>
                          {user.office ? (
                            <span>
                              <FaBuilding className="me-1" />
                              {user.office.name}
                            </span>
                          ) : (
                            <span className="text-muted">-</span>
                          )}
                        </td>
                        <td>
                          <Badge bg={user.is_active ? 'success' : 'secondary'}>
                            {user.is_active ? 'Aktif' : 'Pasif'}
                          </Badge>
                        </td>
                        <td>
                          {user.last_login ? 
                            new Date(user.last_login).toLocaleDateString('tr-TR') : 
                            'Hiç giriş yapmadı'
                          }
                        </td>
                        <td>
                          <Button
                            variant="outline-primary"
                            size="sm"
                            className="me-1"
                            onClick={() => handleEditUser(user)}
                          >
                            <FaEdit />
                          </Button>
                          <Button
                            variant="outline-warning"
                            size="sm"
                            onClick={() => {
                              setCurrentUser(user);
                              setShowPasswordModal(true);
                            }}
                          >
                            <FaKey />
                          </Button>
                        </td>
                      </tr>
                    ))}
                </tbody>
              </Table>
            </Card.Body>
          </Card>
        </Tab>

        {/* Visa Offices Tab - Admin only */}
        {isAdmin && (
          <Tab eventKey="visa-offices" title="Danışmanlık Firmaları">
            <Card>
              <Card.Header className="d-flex justify-content-between align-items-center">
                <h5 className="mb-0">Danışmanlık Firmaları</h5>
                <Button variant="primary" size="sm" onClick={handleCreateOffice}>
                  <FaPlus className="me-1" />
                  Yeni Ofis
                </Button>
              </Card.Header>
              <Card.Body>
                <Table responsive striped hover>
                  <thead>
                    <tr>
                      <th>Ofis Adı</th>
                      <th>Şehir</th>
                      <th>Telefon</th>
                      <th>E-posta</th>
                      <th>Kapasite</th>
                      <th>Kullanıcı Sayısı</th>
                      <th>Durum</th>
                      <th>İşlemler</th>
                    </tr>
                  </thead>
                  <tbody>
                    {visaOffices.map(office => (
                      <tr key={office.id}>
                        <td>
                          <strong>{office.name}</strong>
                        </td>
                        <td>
                          <FaCity className="me-1" />
                          {office.city}
                        </td>
                        <td>{office.phone || '-'}</td>
                        <td>{office.email || '-'}</td>
                        <td>{office.capacity}</td>
                        <td>
                          <Badge bg="info">
                            {office.users ? office.users.length : 0}
                          </Badge>
                        </td>
                        <td>
                          <Badge bg={office.is_active ? 'success' : 'secondary'}>
                            {office.is_active ? 'Aktif' : 'Pasif'}
                          </Badge>
                        </td>
                        <td>
                          <Button
                            variant="outline-primary"
                            size="sm"
                            onClick={() => handleEditOffice(office)}
                          >
                            <FaEdit />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </Card.Body>
            </Card>
          </Tab>
        )}

        {/* System Tools Tab - Admin only */}
        {isAdmin && (
          <Tab eventKey="tools" title={<><FaCog className="me-1" />Sistem Araçları</>}>
            <Row className="g-4">
              <Col md={6}>
                <Card>
                  <Card.Header>
                    <h5 className="mb-0">
                      <FaBroom className="me-2" />
                      Veritabanı Temizliği
                    </h5>
                  </Card.Header>
                  <Card.Body>
                    <p>Eski logları ve kullanılmayan verileri temizle</p>
                    <Button variant="warning">
                      <FaBroom className="me-1" />
                      Temizlik Başlat
                    </Button>
                  </Card.Body>
                </Card>
              </Col>
              <Col md={6}>
                <Card>
                  <Card.Header>
                    <h5 className="mb-0">
                      <FaDownload className="me-2" />
                      Veri İndirme
                    </h5>
                  </Card.Header>
                  <Card.Body>
                    <p>Sistem verilerini dışa aktır</p>
                    <Button variant="info">
                      <FaDownload className="me-1" />
                      Rapor İndir
                    </Button>
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          </Tab>
        )}
      </Tabs>

      {/* Office Modal */}
      <Modal show={showOfficeModal} onHide={() => setShowOfficeModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            {modalMode === 'create' ? 'Yeni Danışmanlık Firması' : 'Danışmanlık Firması Düzenle'}
          </Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleOfficeSubmit}>
          <Modal.Body>
            <Row className="g-3">
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Ofis Adı *</Form.Label>
                  <Form.Control
                    type="text"
                    value={officeFormData.name}
                    onChange={(e) => setOfficeFormData(prev => ({ ...prev, name: e.target.value }))}
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Şehir *</Form.Label>
                  <Form.Control
                    type="text"
                    value={officeFormData.city}
                    onChange={(e) => setOfficeFormData(prev => ({ ...prev, city: e.target.value }))}
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={12}>
                <Form.Group>
                  <Form.Label>Adres</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={2}
                    value={officeFormData.address}
                    onChange={(e) => setOfficeFormData(prev => ({ ...prev, address: e.target.value }))}
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Telefon</Form.Label>
                  <Form.Control
                    type="text"
                    value={officeFormData.phone}
                    onChange={(e) => setOfficeFormData(prev => ({ ...prev, phone: e.target.value }))}
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label>E-posta</Form.Label>
                  <Form.Control
                    type="email"
                    value={officeFormData.email}
                    onChange={(e) => setOfficeFormData(prev => ({ ...prev, email: e.target.value }))}
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Kapasite</Form.Label>
                  <Form.Control
                    type="number"
                    min="1"
                    value={officeFormData.capacity}
                    onChange={(e) => setOfficeFormData(prev => ({ ...prev, capacity: parseInt(e.target.value) || 50 }))}
                  />
                </Form.Group>
              </Col>
            </Row>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowOfficeModal(false)}>
              İptal
            </Button>
            <Button variant="primary" type="submit">
              {modalMode === 'create' ? 'Oluştur' : 'Güncelle'}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* User Modal */}
      <Modal show={showUserModal} onHide={() => setShowUserModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            {modalMode === 'create' ? 'Yeni Kullanıcı' : 'Kullanıcı Düzenle'}
          </Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleUserSubmit}>
          <Modal.Body>
            <Row className="g-3">
              <Col md={6}>
                <Form.Group>
                  <Form.Label>E-posta *</Form.Label>
                  <Form.Control
                    type="email"
                    value={userFormData.email}
                    onChange={(e) => setUserFormData(prev => ({ ...prev, email: e.target.value }))}
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Şifre {modalMode === 'create' ? '*' : '(değiştirmek için doldurun)'}</Form.Label>
                  <Form.Control
                    type="password"
                    value={userFormData.password}
                    onChange={(e) => setUserFormData(prev => ({ ...prev, password: e.target.value }))}
                    required={modalMode === 'create'}
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Ad *</Form.Label>
                  <Form.Control
                    type="text"
                    value={userFormData.first_name}
                    onChange={(e) => setUserFormData(prev => ({ ...prev, first_name: e.target.value }))}
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Soyad *</Form.Label>
                  <Form.Control
                    type="text"
                    value={userFormData.last_name}
                    onChange={(e) => setUserFormData(prev => ({ ...prev, last_name: e.target.value }))}
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Telefon</Form.Label>
                  <Form.Control
                    type="text"
                    value={userFormData.phone}
                    onChange={(e) => setUserFormData(prev => ({ ...prev, phone: e.target.value }))}
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Rol *</Form.Label>
                  <Form.Select
                    value={userFormData.role}
                    onChange={(e) => setUserFormData(prev => ({ ...prev, role: e.target.value }))}
                    required
                  >
                    <option value="user">Kullanıcı</option>
                    <option value="visa_office">Danışmanlık Firması</option>
                    {isAdmin && <option value="admin">Admin</option>}
                  </Form.Select>
                </Form.Group>
              </Col>
              {(userFormData.role === 'visa_office' || userFormData.role === 'user') && (
                <Col md={6}>
                  <Form.Group>
                    <Form.Label>Danışmanlık Firması</Form.Label>
                    <Form.Select
                      value={userFormData.office_id}
                      onChange={(e) => setUserFormData(prev => ({ ...prev, office_id: e.target.value }))}
                      disabled={isVisaOffice} // Visa office users can't change office
                    >
                      <option value="">Firma seçin...</option>
                      {visaOffices.map(office => (
                        <option key={office.id} value={office.id}>
                          {office.name} - {office.city}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>
              )}
            </Row>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowUserModal(false)}>
              İptal
            </Button>
            <Button variant="primary" type="submit">
              {modalMode === 'create' ? 'Oluştur' : 'Güncelle'}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* Password Modal */}
      <Modal show={showPasswordModal} onHide={() => setShowPasswordModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Şifre Değiştir</Modal.Title>
        </Modal.Header>
        <Form onSubmit={async (e) => {
          e.preventDefault();
          try {
            await apiCall('PUT', `/api/admin/panel-users/${currentUser.id}`, {
              password: passwordFormData.new_password
            });
            toast.success('Şifre başarıyla değiştirildi');
            setShowPasswordModal(false);
            setPasswordFormData({ new_password: '' });
          } catch (error) {
            toast.error(error.message || 'Şifre değiştirme başarısız');
          }
        }}>
          <Modal.Body>
            <Form.Group>
              <Form.Label>Yeni Şifre</Form.Label>
              <Form.Control
                type="password"
                value={passwordFormData.new_password}
                onChange={(e) => setPasswordFormData(prev => ({ ...prev, new_password: e.target.value }))}
                required
                minLength={6}
              />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowPasswordModal(false)}>
              İptal
            </Button>
            <Button variant="primary" type="submit">
              Şifreyi Değiştir
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Kullanıcı Deaktivasyonu</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>Bu kullanıcıyı deaktive etmek istediğinizden emin misiniz?</p>
          {currentUser && (
            <div className="bg-light p-3 rounded">
              <strong>Kullanıcı:</strong> {currentUser.first_name} {currentUser.last_name}<br />
              <strong>Email:</strong> {currentUser.email}<br />
              <strong>Rol:</strong> {getRoleBadge(currentUser.role).text}
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
            İptal
          </Button>
          <Button variant="danger" onClick={confirmDeleteUser}>
            Deaktive Et
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default Admin; 